import type { ReactNode } from 'react';
import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';

/**
 * 后台的基础组件。
 *
 * 与用户侧同一套视觉语言（白卡、软蓝底、实心品牌蓝按钮），但更紧凑：
 * 运营在后台看的是密度更高的列表，而不是营销页。
 */

export function Button({
  children,
  variant = 'secondary',
  onClick,
  disabled,
  loading,
  type = 'button',
  size = 'md',
  block = false,
}: {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit';
  size?: 'md' | 'sm';
  block?: boolean;
}) {
  const variants = {
    primary: 'bg-brand text-white hover:bg-brand-hover disabled:bg-brand/40',
    secondary: 'border border-line bg-panel text-fg hover:border-brand hover:text-brand',
    ghost: 'text-fg-muted hover:bg-canvas hover:text-fg',
    danger: 'bg-danger text-white hover:opacity-90',
  } as const;
  const sizes = { md: 'h-9 px-3.5 text-sm', sm: 'h-8 px-2.5 text-[13px]' } as const;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${variants[variant]} ${sizes[size]} ${block ? 'w-full' : ''}`}
    >
      {loading ? (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : null}
      {children}
    </button>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl bg-panel p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] ${className}`}>
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold text-fg">{title}</h2>
        {hint ? <p className="mt-1 text-xs text-fg-muted">{hint}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-[22px] font-semibold text-fg">{title}</h1>
        {description ? <p className="mt-1 text-[13px] text-fg-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

const BADGE_TONES = {
  neutral: 'bg-canvas text-fg-muted',
  brand: 'bg-brand/10 text-brand',
  ok: 'bg-ok/10 text-ok',
  danger: 'bg-danger/10 text-danger',
  warn: 'bg-warn/10 text-warn',
} as const;

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: keyof typeof BADGE_TONES;
}) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs ${BADGE_TONES[tone]}`}>
      {children}
    </span>
  );
}

export function Notice({
  children,
  tone = 'info',
}: {
  children: ReactNode;
  tone?: 'info' | 'warn' | 'danger' | 'ok';
}) {
  const tones = {
    info: 'bg-brand/5 text-fg',
    warn: 'bg-warn/10 text-fg',
    danger: 'bg-danger/10 text-fg',
    ok: 'bg-ok/10 text-fg',
  } as const;
  return <div className={`rounded-md px-3 py-2 text-[13px] ${tones[tone]}`}>{children}</div>;
}

export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-[13px] font-medium text-fg">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-fg-muted">{hint}</p>
      ) : null}
    </div>
  );
}

const CONTROL =
  'w-full rounded-md border border-line bg-panel px-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand focus:outline-none disabled:bg-canvas';

export function Input({
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  maxLength,
  onEnter,
}: {
  id?: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'number';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  maxLength?: number;
  onEnter?: () => void;
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && onEnter) {
          event.preventDefault();
          onEnter();
        }
      }}
      placeholder={placeholder}
      autoComplete={autoComplete}
      maxLength={maxLength}
      className={`${CONTROL} h-9`}
    />
  );
}

export function Textarea({
  id,
  value,
  onChange,
  placeholder,
  rows = 4,
  maxLength,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
}) {
  return (
    <textarea
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className={`${CONTROL} resize-y py-2 leading-relaxed`}
    />
  );
}

export function Select<T extends string>({
  id,
  value,
  onChange,
  options,
  disabled,
}: {
  id?: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as T)}
      className={`${CONTROL} h-9`}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-[13px] text-fg">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-[#1366ec]"
      />
      {label}
    </label>
  );
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // portal：避免被祖先的 transform / overflow 裁切。
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div
        className={`relative w-full ${width} max-h-[85vh] overflow-y-auto rounded-xl bg-panel p-5 shadow-xl`}
      >
        <div className="mb-3">
          <h2 className="text-[16px] font-semibold text-fg">{title}</h2>
          {description ? <p className="mt-1 text-[13px] text-fg-muted">{description}</p> : null}
        </div>
        {children}
        {footer ? <div className="mt-5 flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  width?: string;
}

export function Table<T>({
  columns,
  rows,
  getKey,
  empty,
  loading,
}: {
  columns: Column<T>[];
  rows: T[];
  getKey: (row: T) => string;
  empty?: string;
  loading?: boolean;
}) {
  if (loading) {
    return <div className="h-24 animate-pulse rounded-md bg-canvas" />;
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-md bg-canvas px-4 py-8 text-center text-[13px] text-fg-muted">
        {empty ?? '暂无数据'}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-[13px]">
        <thead>
          <tr className="border-b border-line text-left text-xs text-fg-muted">
            {columns.map((column) => (
              <th
                key={column.key}
                className="py-2 pr-3 font-medium"
                style={{ width: column.width }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={getKey(row)} className="border-b border-line/60 last:border-0">
              {columns.map((column) => (
                <td key={column.key} className="py-2.5 pr-3 align-middle text-fg">
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  return (
    <div className="mt-3 flex items-center justify-between text-xs text-fg-muted">
      <span>
        共 {total} 条 · 第 {page}/{pages} 页
      </span>
      <div className="flex gap-2">
        <Button size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          上一页
        </Button>
        <Button size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>
          下一页
        </Button>
      </div>
    </div>
  );
}

export function Skeleton({ className = 'h-24 w-full' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-canvas ${className}`} />;
}
